import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CUIColors } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../../context/analytics-context';
import {
  setIsImageUploadToPhotoZoneDrawerOpen,
  setIsWamDrawerOpen,
  setPhotoDrawerClosed,
  useAppContext,
} from '../../../context/app-context';
import { useUIStore } from '../../../stores/ui';
import { setSelectedPhotoZone, useCardContext } from '../../../context/card-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { usePhotoTextZoneControls } from '../../../features/card-editor/hooks/use-photo-text-zone-controls';
import { useTextArea } from '../../../features/card-editor/hooks/use-text-area';
import { handlePhototextzoneSelection } from '../../../features/card-editor/utils/handle-phototextzone-selection';
import { useFeatureFlags, useIsSignAndSend } from '../../../hooks';
import { ActionType, useDatadog } from '../../../hooks/use-datadog';
import colorsList from '../../../styles/util.scss';
import {
  addUserTextHandlers,
  CanvasDataTypes,
  convertPointToPixel,
  getFontidByUrlName,
  getGroupedItemByName,
} from '../../../utils';
import { getCardFaceClipPath } from '../../../utils/canvas-utils/get-card-face-clip-path';
import { parseTextAction } from '../../../utils/parse-text-action';
import { getTextSettings, removeEditableAreaButtons } from '../../card-editor/utils';
import { isFloatingToolMenuInteraction } from '../../floating-tool-menu/floating-tool-menu-host';
import { isPendingOverlayReselect } from '../../../features/card-editor/components/overlay-textbox/pending-overlay-reselect';
import { ResetTextData } from '../card-face-types';
import { createTextElement } from '../utils/create-text-element';

type UseCanvasMouseUpProps = {
  cardIndex: number;
  fabric: typeof import('fabric').fabric;
  editingHybridBox: React.MutableRefObject<boolean>;
  setIsResetTextModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useCanvasMouseUp = ({
  cardIndex,
  fabric,
  editingHybridBox,
  setIsResetTextModalOpen,
}: UseCanvasMouseUpProps) => {
  const { initializedDataState } = useInitializationDataContext();
  const { cardState, cardDispatch } = useCardContext();
  const { appDispatch, appState } = useAppContext();
  // mouse:up is bound once on canvas init, so its closure captures a stale
  // appState. Read the latest value through a ref to avoid that.
  const appStateRef = useRef(appState);
  appStateRef.current = appState;
  const openTextDrawer = useUIStore((state) => state.openTextDrawer);
  const { trackPhotoUploadMethod } = useAnalyticsContext();
  const { t } = useTranslation();
  const { addCustomTextArea } = useTextArea();
  const {
    IS_TEXTBOX_OVERLAY_ENABLED,
    IS_POD_TEXT_ZONE_OVERLAY_ENABLED,
    IS_POD_USER_TEXTBOX_OVERLAY_ENABLED,
    IS_SNS_TEXTBOX_OVERLAY_ENABLED,
  } = useFeatureFlags();
  const { handleTypeAMessage, handleAddHandwriting } = usePhotoTextZoneControls();
  const { addAction, textAction } = useDatadog();
  const isSignAndSend = useIsSignAndSend();
  const [resetTextData, setResetTextData] = useState<ResetTextData>({
    canvas: null,
    zoneName: '',
  });
  const { isUK } = initializedDataState;
  const defaultTextAreaSettings = { ...initializedDataState.data?.variables.template_data.DefaultTextArea };

  const handleMouseUp = ({ options, canvasRef, canvas, overlayTextAreaRef }) => {
    const eventTarget = options?.e?.target;
    const floatingMenuTarget = eventTarget instanceof Element ? eventTarget : null;

    if (isFloatingToolMenuInteraction(floatingMenuTarget)) {
      return;
    }

    // Close the Photo Drawer when the user clicks anywhere on the canvas that is
    // not the add-photo (+) button of a zone. The card is tiled with grey
    // PhotoZone rects, so only the button itself should keep the drawer open.
    const latestAppState = appStateRef.current;
    const isPhotoDrawerOpen = latestAppState?.isImageUploadDrawerOpen || latestAppState?.isImageUploadToPhotoZoneOpen;
    const clickedType = options?.target?.data?.type;
    const isAddPhotoButton = clickedType === CanvasDataTypes.PhotoZoneButton;
    if (isPhotoDrawerOpen && !isAddPhotoButton) {
      setPhotoDrawerClosed(appDispatch);
      setSelectedPhotoZone(cardDispatch, null, null);
    }

    if (
      !isSignAndSend &&
      IS_TEXTBOX_OVERLAY_ENABLED &&
      IS_POD_TEXT_ZONE_OVERLAY_ENABLED &&
      options?.target?.data?.type === CanvasDataTypes.EditableText
    ) {
      // Open POD template text after Fabric finishes selecting it.
      const editableText = options.target as fabric.Textbox;
      canvas?.setActiveObject(editableText);
      overlayTextAreaRef?.current?.onShowOverlayTextarea({ fabricTextbox: editableText });
      canvas?.renderAll();
      return;
    }

    // PRCM-10837: empty-canvas tap during text-overlay edit. Blur is shared
    // POD/S&S; the pending-flag + discard fallbacks are POD-only (select mode).
    const isPodOverlayFlow = IS_TEXTBOX_OVERLAY_ENABLED && IS_POD_USER_TEXTBOX_OVERLAY_ENABLED;
    const isSnsOverlayFlow = isSignAndSend && IS_TEXTBOX_OVERLAY_ENABLED && IS_SNS_TEXTBOX_OVERLAY_ENABLED;
    if (!options?.target && (isPodOverlayFlow || isSnsOverlayFlow)) {
      const isOverlayVisible = overlayTextAreaRef?.current?.isOverlayTextareaVisible();
      if (isOverlayVisible) {
        // Mobile touch path — native blur hasn't fired yet.
        overlayTextAreaRef.current?.onTextareaBlur();
        return;
      }
      if (isPodOverlayFlow) {
        if (isPendingOverlayReselect()) {
          // Desktop path — outside-click handler will consume the flag.
          return;
        }
        // POD select mode: deselect (drawer closes via selection:cleared).
        canvas?.discardActiveObject();
        canvas?.renderAll();
      }
      return;
    }

    //  handles the uploading image for a photo zone
    if (
      options?.target?.data?.type === CanvasDataTypes.PhotoTextZone &&
      isSignAndSend &&
      IS_SNS_TEXTBOX_OVERLAY_ENABLED
    ) {
      const originalTextbox = getGroupedItemByName(CanvasDataTypes.PhotoZoneTextbox, options.target) as fabric.Textbox;
      canvas?.setActiveObject(options.target);
      if (originalTextbox && originalTextbox.text !== '') {
        // Add defensive check to ensure textbox has required data.ID before entering editing mode
        if (!originalTextbox.data?.ID) {
          // eslint-disable-next-line no-console
          console.warn('[useCanvasMouseUp] Cannot enter editing mode: PhotoZoneTextbox missing data.ID', {
            hasTextbox: !!originalTextbox,
            textboxName: originalTextbox.name,
            textboxType: originalTextbox.data?.type,
          });
          return;
        }
        openTextDrawer();
        originalTextbox.enterEditing();
      }
    }

    if (options?.target?.data?.type === CanvasDataTypes.PhotoZoneButton) {
      setSelectedPhotoZone(cardDispatch, options.target.data.photoZoneId);
      setIsImageUploadToPhotoZoneDrawerOpen(appDispatch);
      trackPhotoUploadMethod('camera icon');
    } else if (options?.target?.data?.type === CanvasDataTypes.UserZoneAddTextButton) {
      // Adds an editable text box to the user zone
      if (canvas && canvasRef.current) {
        if (IS_TEXTBOX_OVERLAY_ENABLED && IS_POD_USER_TEXTBOX_OVERLAY_ENABLED) {
          const currentCardFace = cardState.cardFacesList[`${cardIndex}`];
          addCustomTextArea({
            canvas: canvasRef,
            cardFace: currentCardFace,
            onError: () => {
              throw new Error('Error adding custom text area');
            },
          });
        } else {
          const INITIAL_TEXT_MESSAGE = `${t('cardEditor.userTextDefaultMessage')}`;
          const cardFaceClipPath = getCardFaceClipPath(cardState.cardFacesList[`${cardIndex}`], 0);
          const defaultFallbackColor = isUK
            ? colorsList[CUIColors.CharcoalGrey]
            : initializedDataState.defaultColor || colorsList[CUIColors.Black];
          const defaultColor = defaultTextAreaSettings?.TextColor || defaultFallbackColor;
          const defaultFontSize = convertPointToPixel(
            initializedDataState.data?.variables.template_data.DefaultTextArea.FontSize || 50,
          );
          defaultTextAreaSettings.FontFamilyId = getFontidByUrlName(
            defaultTextAreaSettings?.FontFamily || '',
            initializedDataState.data?.font_collection.fonts,
          );

          const defaultTextSettings = getTextSettings({
            cardState,
            color: defaultColor,
            defaultFontSize,
            isUK,
            defaultOptions: defaultTextAreaSettings,
            name: CanvasDataTypes.UserText,
          });
          const width = cardFaceClipPath.width;

          const initialTextMessage = defaultTextSettings.Text || INITIAL_TEXT_MESSAGE;
          const textElement = createTextElement({
            width,
            fabric,
            initialTextMessage,
            isUK,
            defaultTextSettings,
            cardFaceClipPath,
            cardState,
          });

          addUserTextHandlers(textElement, initialTextMessage, defaultColor, canvasRef.current);
          textAction(ActionType.ADD_TEXT, parseTextAction(textElement));
          canvasRef.current.add(textElement);
          canvasRef.current.setActiveObject(textElement);
        }
      }
      removeEditableAreaButtons(canvas);
    } else if (options?.target?.data?.type === CanvasDataTypes.UserZoneAddWamButton) {
      setIsWamDrawerOpen(appDispatch);
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZoneTextButton) {
      editingHybridBox.current = true;
      handleTypeAMessage({
        canvas,
        button: options?.target,
      });
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZoneWAMButton) {
      handleAddHandwriting({
        canvas,
        button: options?.target,
      });
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZoneResetButton) {
      if (canvas) {
        setIsResetTextModalOpen(true);
        setResetTextData({
          canvas: canvas,
          zoneName: options?.target?.data?.linkedZoneName,
        });
      }
    } else if (
      options?.target?.data?.type === CanvasDataTypes.PhotoTextZone &&
      !(isSignAndSend && IS_SNS_TEXTBOX_OVERLAY_ENABLED)
    ) {
      const photoTextZone = options.target as fabric.Group;
      handlePhototextzoneSelection({
        photoTextZone,
        canvas,
      });
    } else if (
      options?.target?.data?.type === CanvasDataTypes.PhotoZoneImage ||
      options?.target?.data?.type === CanvasDataTypes.UserImage
    ) {
      addAction('select-image', {
        image: options.target.data,
        cardFace: cardState.cardFacesList[`${cardState.activeCardIndex}`]?.type ?? null,
        zoneId: options.target.data.photoZoneId || null,
      });
    }
  };

  return { resetTextData, handleMouseUp };
};
